/**
 * buildGanttFigure — turns a PlanTimelineResponse into Plotly horizontal-bar
 * traces + layout for the multi-track Gantt (§13/§15, viz stack = Plotly).
 *
 * Each track is a y-category row (top-to-bottom in response order). A bar is a
 * horizontal segment drawn with `base` (start) + width (duration). Two overlaid
 * layers per operation:
 *   • PLAN   — outlined / translucent (the program), offset to the row's top.
 *   • ACTUAL — solid fill (what happened / is happening), offset to the bottom.
 * NPT bars use --crit; offline bars get a hatched-looking translucent style on
 * the adjacent well's track. A "Today" line + the plan/actual filter come from
 * the caller. Colours are resolved from the --op-* tokens by the component and
 * passed in, so this stays a pure, DOM-free transform (unit-testable).
 */
import type { Data, Layout, Shape } from "plotly.js-dist-min";
import type { PlanTimelineResponse, PlanTimelineBar } from "@api/types";
import {
  plannedSpan,
  actualSpan,
  timelineSpan,
  autoScale,
  tickSpec,
  isCpeTrack,
  type PlanActualMode,
  type ZoomScale,
} from "./plannerModel";

/** Palette the component resolves from CSS tokens and hands in. */
export interface GanttPalette {
  drilling: string;
  completion: string;
  pilot: string;
  workover: string;
  pulling: string;
  wireline: string;
  ct: string;
  pad: string;
  unconv: string;
  npt: string;
  /** CPE spine track band tint + the ink/dim chrome. */
  spine: string;
  ink: string;
  dim: string;
  line: string;
  fontMono: string;
}

const ROW_H = 34;
const BAR_H = 0.28; // half-height of a plan/actual sub-bar within a row

function opColor(bar: PlanTimelineBar, pal: GanttPalette): string {
  if (bar.is_npt) return pal.npt;
  const map: Record<string, string> = {
    drilling: pal.drilling,
    completion: pal.completion,
    pilot: pal.pilot,
    workover: pal.workover,
    pulling: pal.pulling,
    wireline: pal.wireline,
    ct: pal.ct,
    pad: pal.pad,
    unconv: pal.unconv,
    npt: pal.npt,
  };
  return map[bar.op_type] ?? pal.drilling;
}

/** rgba() with an alpha applied to a hex or rgb() base colour. */
function withAlpha(color: string, alpha: number): string {
  const hex = color.trim();
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (m?.[1]) {
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const rgb = /^rgb\(([^)]+)\)$/i.exec(hex);
  if (rgb?.[1]) return `rgba(${rgb[1]}, ${alpha})`;
  return hex;
}

export interface GanttFigure {
  data: Data[];
  layout: Partial<Layout>;
  /** Rendered plot height (px) — grows with the track count. */
  height: number;
}

/**
 * Build the figure. `scale` fixes the axis tick regime; when `autoZoom` is on we
 * derive it from the data span instead (§17 fit-to-data).
 */
export function buildGanttFigure(
  resp: PlanTimelineResponse,
  opts: {
    palette: GanttPalette;
    now: number;
    mode: PlanActualMode;
    scale: ZoomScale;
    autoZoom: boolean;
  },
): GanttFigure {
  const { palette: pal, now, mode } = opts;
  const span = timelineSpan(resp, now);
  const scale = opts.autoZoom && span ? autoScale(span) : opts.scale;

  // y categories, response order, but Plotly stacks categories bottom-up — so we
  // reverse to render the first track at the top.
  const tracks = resp.tracks ?? [];
  const yCats = tracks.map((t) => t.label);
  const rev = [...yCats].reverse();

  const planBars: {
    base: number[];
    x: number[];
    y: string[];
    color: string[];
    line: string[];
    text: string[];
  } = { base: [], x: [], y: [], color: [], line: [], text: [] };
  const actualBars: {
    base: number[];
    x: number[];
    y: string[];
    color: string[];
    text: string[];
    pattern: (string | "")[];
  } = { base: [], x: [], y: [], color: [], text: [], pattern: [] };

  for (const track of tracks) {
    for (const bar of track.bars ?? []) {
      const base = opColor(bar, pal);
      if (mode !== "actual") {
        const p = plannedSpan(bar);
        if (p) {
          planBars.base.push(p.start);
          planBars.x.push(p.end - p.start);
          planBars.y.push(track.label);
          planBars.color.push(withAlpha(base, 0.16));
          planBars.line.push(base);
          planBars.text.push(`${bar.label} · plan`);
        }
      }
      if (mode !== "plan") {
        const a = actualSpan(bar, now);
        if (a) {
          actualBars.base.push(a.start);
          actualBars.x.push(Math.max(a.end - a.start, 0));
          actualBars.y.push(track.label);
          actualBars.color.push(bar.is_offline ? withAlpha(base, 0.55) : base);
          actualBars.pattern.push(bar.is_offline ? "/" : "");
          actualBars.text.push(
            `${bar.label} · actual${bar.is_offline ? " (offline)" : ""}`,
          );
        }
      }
    }
  }

  const data: Data[] = [];
  // Plan layer — offset UP within the row via a positive base offset on y.
  if (planBars.base.length) {
    data.push({
      type: "bar",
      orientation: "h",
      name: "Plan",
      base: planBars.base,
      x: planBars.x,
      y: planBars.y,
      width: BAR_H,
      offset: 0.02,
      marker: {
        color: planBars.color,
        line: { color: planBars.line, width: 1.2 },
      },
      text: planBars.text,
      hovertemplate: "%{text}<extra></extra>",
      showlegend: false,
    } as Data);
  }
  // Actual layer — offset DOWN within the row.
  if (actualBars.base.length) {
    data.push({
      type: "bar",
      orientation: "h",
      name: "Actual",
      base: actualBars.base,
      x: actualBars.x,
      y: actualBars.y,
      width: BAR_H,
      offset: -BAR_H - 0.02,
      marker: {
        color: actualBars.color,
        line: { color: pal.line, width: 0.5 },
      },
      text: actualBars.text,
      hovertemplate: "%{text}<extra></extra>",
      showlegend: false,
    } as Data);
  }

  const tick = tickSpec(scale);

  // CPE spine tracks get a faint background band so the constraining resource
  // reads as the timeline's spine (§14).
  const shapes: Partial<Shape>[] = [];
  tracks.forEach((track, i) => {
    if (isCpeTrack(track)) {
      // category index from the bottom (Plotly y order is reversed)
      const idxFromBottom = tracks.length - 1 - i;
      shapes.push({
        type: "rect",
        xref: "paper",
        yref: "y",
        x0: 0,
        x1: 1,
        y0: idxFromBottom - 0.5,
        y1: idxFromBottom + 0.5,
        fillcolor: withAlpha(pal.spine, 0.12),
        line: { width: 0 },
        layer: "below",
      });
    }
  });

  // Today line (§13) — a vertical marker at `now`, only when in the data span.
  if (span && now >= span.start && now <= span.end) {
    shapes.push({
      type: "line",
      xref: "x",
      yref: "paper",
      x0: now,
      x1: now,
      y0: 0,
      y1: 1,
      line: { color: pal.npt, width: 1.5, dash: "dot" },
      layer: "above",
    });
  }

  const height = Math.max(tracks.length * ROW_H + 60, 160);

  const layout: Partial<Layout> = {
    barmode: "overlay",
    bargap: 0,
    height,
    margin: { l: 8, r: 16, t: 28, b: 36 },
    shapes,
    xaxis: {
      type: "date",
      dtick: tick.dtick,
      tickformat: tick.format,
      showgrid: true,
      side: "top",
      range: span ? [span.start - (span.end - span.start) * 0.02, span.end] : undefined,
    },
    yaxis: {
      type: "category",
      categoryarray: rev,
      categoryorder: "array",
      showgrid: false,
      // Track labels live in the left rail (rendered by the component), so hide
      // the plot's own y ticks to avoid duplicating them.
      showticklabels: false,
    },
    annotations:
      span && now >= span.start && now <= span.end
        ? [
            {
              x: now,
              y: 1,
              xref: "x",
              yref: "paper",
              text: "Today",
              showarrow: false,
              yanchor: "bottom",
              font: { size: 10, color: pal.npt, family: pal.fontMono },
            },
          ]
        : [],
  };

  return { data, layout, height };
}
